import type {
    ZodObject,
    ZodType,
    input,
    output,
    core
} from 'zod'
import type {
    BaseFormFieldObject,
    FormFieldObject,
    SupportedZodTypes,
    FormDataObject,
    FormInputDataAttrs
} from '../types/form'
import type { ModelRef } from 'vue'
import { ref, toValue } from 'vue'
import { defineRuntimeProps, type RuntimeProps } from './components'
import {
    isZodOptional,
    isZodDefault,
    isZodNullable,
    isZodObject,
    getZodSchemaShape,
    isZodBoolean,
    isZodSimpleType
} from './zod'

/**
 * A function that builds the base of the form field object from a Zod type.
 * @example
 * const result = buildFormFieldObjectBase(z.string().optional().default('foo'))
 * // result: { __r: false, __v: 'foo' }
 * @param type The Zod type to build the form field object from.
 */
export function buildFormFieldObjectBase<T extends SupportedZodTypes>(
    type: T
): BaseFormFieldObject<output<T>>
{
    function _buildFormFieldObjectBase(
        type: T,
        required: BaseFormFieldObject<output<T>>['__r'] | undefined,
        val: BaseFormFieldObject<output<T>>['__v'] | undefined
    ): BaseFormFieldObject<output<T>> {
        if (isZodOptional(type)) {
            return _buildFormFieldObjectBase(
                type.def.innerType,
                required ?? false,
                // @ts-ignore - TODO: for some reason, the type instantiation is "excessively deep and possibly infinite"
                val
            )
        }

        if (isZodDefault(type)) {
            return _buildFormFieldObjectBase(type.def.innerType, required, type.def.defaultValue)
        }

        if (isZodNullable(type)) {
            return _buildFormFieldObjectBase(type.def.innerType, required, val)
        }

        return {
            __r: required ?? true,
            __v: (isZodBoolean(type) ? val ?? false : val) as output<T>,
        }
    }

    return _buildFormFieldObjectBase(type, undefined, undefined)
}

export function convertNullToUndefinedDeep(obj: Record<string, unknown>) {
    for (const key in obj) {
        if (obj[key] === null) obj[key] = undefined
        if (typeof obj[key] === 'object' && !Array.isArray(obj[key]) && obj[key] !== null) {
            convertNullToUndefinedDeep(obj[key] as Record<string, unknown>)
        }
    }
}

interface GenerateFormDataObjectsOptions {
    /**
     * Whether to convert null values to undefined.
     * @default false
     */
    convertNullToUndefined: boolean
}

/**
 * A function that generates a form data object from a Zod schema.
 * The form data object is a custom object used internally by a custom form component.
 * @param schema The Zod schema to generate the form data object from.
 * @param values The initial values for the form data object.
 * @param options The options for generating the form data object.
 * @throws Error If the schema could not be normalized.
 */
export function generateFormDataObjects<T extends ZodObject>(schema: T, values?: Partial<AllowNullDeep<input<T>>>, options?: Partial<GenerateFormDataObjectsOptions>): [FormDataObject<T>, Ref<input<T>>] {
    const valueObject: Ref<Record<string, unknown>> = ref(values ? JSON.parse(JSON.stringify(values)) : {})

    const {
        convertNullToUndefined = false,
    } = options ?? {}

    if (convertNullToUndefined) {
        convertNullToUndefinedDeep(valueObject.value)
    }

    function _generateFormDataObject(schema: core.$ZodShape, valueObjParent: MaybeRefOrGetter<Record<string, unknown>>, pathPrefix?: string): FormDataObject<T> {
        const path = pathPrefix ? `${pathPrefix}.` : ''

        const localValueObjParent = toValue(valueObjParent)

        return Object.fromEntries(
            Object.entries(schema).map(([key, _v]) => {
                const value = _v as SupportedZodTypes | Record<string, SupportedZodTypes>

                // check for z.object and regular records
                if (!isZodSimpleType(value)) {
                    // TODO: remove the type casts
                    const shape = (isZodObject(value as any) ? getZodSchemaShape(value as any) : value) as core.$ZodShape
                    localValueObjParent[key] = localValueObjParent[key] ?? {}

                    if (!shape) return [key, {}]
                    return [key, _generateFormDataObject(shape, localValueObjParent[key] as Record<string, unknown>, `${path}${key}`)]
                }

                const base = buildFormFieldObjectBase(
                    // @ts-ignore - TODO: for some reason, the type instantiation is "excessively deep and possibly infinite"
                    value as SupportedZodTypes
                )

                localValueObjParent[key] = localValueObjParent[key] ?? base.__v

                const formFieldObject = {
                    __f: `${path}${key}`,
                    __r: base.__r,
                }

                return [key, Object.defineProperty(formFieldObject, '__v', {
                    enumerable: true,
                    get: () => localValueObjParent[key],
                    set: (val) => {
                        localValueObjParent[key] = val
                    },
                })]
            })
        ) as FormDataObject<T>

    }

    const shape = getZodSchemaShape(schema)
    if (!shape) throw new Error('[generateFormDataObject]: Could not get shape from schema.')
    return [_generateFormDataObject(shape, valueObject), valueObject as Ref<input<T>>]
}

export function mergeFormValues<T extends ZodType>(_formValues: MaybeRefOrGetter<input<T>>, values: Partial<input<T>> | null | undefined, options?: Partial<{ fallbackToCurrentValues: boolean, convertNullToUndefined: boolean }>) {
    const formValues = toRef(_formValues) as Ref<Partial<input<T>>>
    for (const key in formValues.value) {
        if (typeof formValues.value[key] === 'object' && !Array.isArray(formValues.value[key]) && formValues.value[key] !== null && !((formValues.value[key] as any) instanceof File)) {
            // the form values are actually not of this type since we are already one level deep into
            // the object traversal, however, we don't really care about the type here, so we can safely cast it
            mergeFormValues((() => formValues.value[key]) as MaybeRefOrGetter<input<T>>, values?.[key] as Partial<input<T>> | null | undefined, options)
            continue
        }

        const value = values?.[key] ?? (options?.fallbackToCurrentValues !== false && values && !(key in values) ? formValues.value[key] : values?.[key])

        formValues.value[key] = options?.convertNullToUndefined && value === null ? undefined : value
    }
}

export function getFormFieldObject<T extends FormFieldObject<any>, D extends FormDataObject<any>>(key: string | null | undefined, formData: D): T | null {
    if (!key) return null
    const keys = key.split('.')
    let current = formData as any

    for (const k of keys) {
        if (!current[k]) return null
        current = current[k]
    }

    return current as T
}

export function getFormFieldObjectValue<T extends FormFieldObject<any>>(field: T | ModelRef<T | undefined> | undefined): T['__v'] | null {
    if (!field) return null

    if (isRef(field)) {
        if (!field.value) return null
        return field.value.__v
    }

    return field.__v
}

export function setFormFieldObjectValue<T extends FormFieldObject<any>>(field: T | ModelRef<T | undefined> | undefined, value: T['__v']): FormEvent<T> | null {
    if (!field) return null

    if (isRef(field)) {
        if (!field.value) return null

        field.value.__v = value
        return {
            type: 'change',
            __f: field.value.__f,
        }
    }

    field.__v = value

    return {
        type: 'change',
        __f: field.__f,
    }
}

export type BaseFormElementProps<Value> = {
    modelValue?: Value
    modelModifiers?: Record<string, boolean>
    form?: FormFieldObject<Value>
    formModifiers?: Record<string, boolean>
    /**
     * Whether the input is disabled or not.
     * A disabled input cannot be interacted with & can have a different style.
     */
    disabled?: boolean
    /**
     * Whether the input is loading or not.
     * A loading input cannot be interacted with & can have a different style.
     */
    loading?: boolean
    /**
     * Whether the input is required or not.
     */
    required?: boolean | undefined

    // --------------- Accessibility ---------------
    /**
     * The HTML id for the input element.
     */
    id?: string
    /**
     * The HTML id of the element with the description for the input.
     * (will be used for the `aria-describedby` attribute)
     */
    descriptionId?: string | string[]
    /**
     * The aria-label attribute for the input. Do not set this when
     * a label element is associated with the input.
     */
    ariaLabel?: string
    /**
     * The aria-invalid attribute for the input.
     */
    ariaInvalid?: boolean
    /**
     * The HTML data attributes for the input element.
     */
    dataAttrs?: (keyof FormInputDataAttrs)[] | (keyof FormInputDataAttrs)
}

export const getBaseFormElementRuntimeProps = <T, TProps extends BaseFormElementProps<T>>(modelValueRuntimeType: PropType<T>,options?: ComponentOverrideOptions<any, TProps>): RuntimeProps<Pick<TProps, keyof BaseFormElementProps<T>>> =>
    defineRuntimeProps<BaseFormElementProps<T>>({
        modelValue: { type: modelValueRuntimeType },
        modelModifiers: { type: Object },
        form: { type: Object },
        formModifiers: { type: Object },
        disabled: { type: Boolean },
        loading: { type: Boolean },
        required: { type: Boolean },
        id: { type: String },
        descriptionId: { type: [String, Array] },
        ariaLabel: { type: String },
        ariaInvalid: { type: Boolean },
        // @ts-ignore
        dataAttrs: { type: [String, Array] },
    }, options)

export function computeDataAttributes(attrs: (keyof FormInputDataAttrs)[] | string | undefined): Record<string, string> {
    const _attrs = Array.isArray(attrs) ? attrs : (typeof attrs === 'string' ? [attrs] : undefined)
    if (!_attrs || _attrs.length === 0) return {}

    const dataAttrs: Record<string, string> = {}
    for (const attr of _attrs) {
        dataAttrs[`data-${attr}`] = ''
    }

    return dataAttrs
}
